import IconExplorer from '@apollo/icons/default/IconExplorer.svg';
import IconMetrics from '@apollo/icons/default/IconMetrics.svg';
import {
  Button,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import React from 'react';

import * as graphRoutes from 'src/app/graph/routes';

interface EndpointAddedConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  graphId: string;
  variantName: string;
}

/**
 * Confirmation modal shown after successfully adding an endpoint via deployment flow.
 * Shows next steps for the user including links to subgraphs, explorer, insights, etc.
 */
export const EndpointAddedConfirmationModal = ({
  isOpen,
  onClose,
  graphId,
  variantName,
}: EndpointAddedConfirmationModalProps) => {
  return (
    <Modal size="xl" isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Hooray! Your endpoint has been set.</ModalTitle>
        </ModalHeader>

        <ModalBody className="flex min-h-20 flex-col gap-8 px-6 py-0">
          <div className="flex flex-row items-start gap-2.5">
            <ModalDescription>
              From here, you'll get the most benefit by seeing the relationship
              between your graph and GraphOS's Insights.
            </ModalDescription>
          </div>

          <div className="flex flex-col gap-8">
            <div className="flex flex-row items-start gap-6">
              <div className="flex shrink-0 flex-row items-center justify-center gap-2 rounded-lg border border-primary p-4">
                <IconExplorer className="size-6" />
              </div>
              <div className="flex grow flex-col gap-1">
                <div className="text-heading-lg">
                  Step one: Request data from your graph
                </div>
                <div>
                  Test your graph variant by making and running operations
                  directly in{' '}
                  <Link
                    to={graphRoutes.explorerRouteConfig.path({
                      graphId,
                      graphVariant: variantName,
                    })}
                  >
                    Apollo's Explorer IDE
                  </Link>
                  .
                </div>
              </div>
            </div>

            <div className="flex flex-row items-start gap-6">
              <div className="flex shrink-0 flex-row items-center justify-center gap-2 rounded-lg border border-primary p-4">
                <IconMetrics className="size-6" />
              </div>
              <div className="flex grow flex-col gap-1">
                <div className="text-heading-lg">
                  Step two: Get insights about your request(s)
                </div>
                <div>
                  Head over to{' '}
                  <Link
                    to={graphRoutes.insightsRouteConfig.path({
                      graphId,
                      graphVariant: variantName,
                      tab: 'operation',
                    })}
                  >
                    Insights
                  </Link>{' '}
                  to explore how graph activity shapes performance in real time!
                </div>
              </div>
            </div>
          </div>
        </ModalBody>

        <ModalFooter>
          <Button variant="primary" onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
